import { GUIDE_TRIGGER_CHANNEL, isGuideTrigger, type GuideTrigger } from "./triggers.js";

/**
 * 🔴🔴 US-AIV2-0063-S01 (@UC0184) — KÊNH NGOÀI CHAT của Người dẫn đường: **luật**, không phải đường ống.
 *
 * Trợ lý chỉ nói được khi người dùng mở nó ra. Bốn trong 17 trigger của `triggers.ts` là **thời gian trôi**
 * (`DEADLINE_NEAR` · `STALE` · `PERIOD_START` · `MILESTONE`) và ba là **người khác làm gì đó** — không lượt chat nào bắn được
 * chúng. Đây là chỗ khai ai lo cái nào, và nó là một **bảng ĐÓNG có kiểu**: thiếu một trigger kênh `push` là **TS đỏ**, không
 * phải một tình huống im lặng vĩnh viễn (cùng kỷ luật `GUIDE_SILENT_TRIGGERS` của `cards.ts`).
 *
 * ═══ 🔴 BA PHÁT HIỆN ĐỔI THIẾT KẾ SO VỚI HỒ SƠ TASK — đọc trước khi "sửa lại cho giống hồ sơ" ═══
 *
 * Hồ sơ `US-AIV2-0063/index.md` viết *"job worker `guide-notify` … **bắn `journey()`** ở 3 trigger"*. Đo trên mã thật thì
 * **worker KHÔNG gọi `journey()` được**, và cả ba lý do đều là bất biến của repo chứ không phải bất tiện:
 *
 *  1. **`journey()` đòi `allowedPages`** — danh sách trang đã qua **cổng năng lực** của CHÍNH người đó
 *     (`AiCapabilityGateService`, chỉ có ở `apps/api`, +N truy vấn mỗi người). Worker không có nó, và truyền `[]` là khai
 *     *"đã hỏi và người này không có cửa nào"* trong khi sự thật là *"chưa hỏi"*. Đó đúng lớp lỗi `0 ≠ chưa biết` mà
 *     `journey-context.loader.ts` phải vá hai vòng ở review #826 (HIGH-1/HIGH-2, `unknownSignals`).
 *  2. **Thẻ chỉ có `templateVi`** — tiếng Việt, một bản. Notification **phải render theo locale NGƯỜI NHẬN ở worker**
 *     (`CLAUDE.md` §7, `renderNotif` + `pickRecipientLocale`). Đổ `GuideCard.templateVi` vào `notifications.body` là ép mọi
 *     người nhận đọc tiếng Việt và dựng một nguồn câu chữ THỨ HAI cạnh `NOTIFICATION_TEMPLATE_REGISTRY`.
 *  3. **Bối cảnh của `journey()` là 7 phép đếm/người** — chính `context.ts` đã ghi cảnh báo có địa chỉ cho task này:
 *     *"`0063` nối kênh worker chạy phép đếm này theo LỊCH ⇒ phải có index hoặc đếm hai bước TRƯỚC lúc đó"*
 *     (`pendingConfirmations` đi qua ba quan hệ lồng, không index từ `groupId`).
 *
 * ⇒ Kênh này **phát hiện TÍN HIỆU theo tập** (một truy vấn cho cả hệ thống, không phải N lượt `journey()`), rồi giao cho
 * **đúng đường ống notification đang có**: `outbox_events` → `outbox-dispatcher` → `fanOutNotifications` → template registry.
 * Cái được tái dùng là **đường ống**; cái được viết mới chỉ là **vị-từ tín hiệu**. Không có engine notification thứ hai.
 *
 * ═══ 🔴 VÀ ĐÂY LÀ PHÁT HIỆN QUAN TRỌNG NHẤT: PHẦN LỚN "KÊNH PUSH" ĐÃ CÓ NGƯỜI LÀM ═══
 *
 * `DEADLINE_NEAR` — *"nhắc khi tới mốc `reminder_days_before` ngày trước `Collection.dueDate`"* — là **nguyên văn mô tả của
 * `reminder.job.ts`** (`due.reminder`, có từ US-E9.F2-0058): riêng từng người, idempotent theo due, ngưỡng cấu hình được
 * fund > system. Viết một vòng quét hạn thứ hai ở đây là dựng đúng thứ `CLAUDE.md` §3.8 cấm — *"KHÔNG engine thu thứ hai"* —
 * và hậu quả không phải mã thừa mà là **hai lời nhắc cho một khoản nợ**.
 *
 * Nên bảng dưới có ba trạng thái, và `covered` phải nêu ĐÍCH DANH event đã phủ (một lời hứa suông không ngăn được ai):
 *   · `live`    — kênh này bắn, có `eventType` riêng;
 *   · `covered` — đã có thông báo khác phủ đúng tình huống đó ⇒ **cấm** bắn thêm;
 *   · `silent`  — chưa ai lo, có CHỦ và có lý do (ratchet cùng họ `GUIDE_SILENT_TRIGGERS`).
 */

/** Trigger đi qua kênh NGOÀI chat — SUY từ `GUIDE_TRIGGER_CHANNEL`, không khai tay (bản chép thứ hai sẽ lệch). */
export type GuidePushTrigger = {
  [K in GuideTrigger]: (typeof GUIDE_TRIGGER_CHANNEL)[K] extends "push" ? K : never;
}[GuideTrigger];

export const GUIDE_PUSH_TRIGGERS: readonly GuidePushTrigger[] = Object.freeze(
  (Object.keys(GUIDE_TRIGGER_CHANNEL) as GuideTrigger[]).filter(
    (t): t is GuidePushTrigger => GUIDE_TRIGGER_CHANNEL[t] === "push",
  ),
);

export type GuideNotifyState =
  /** Kênh này bắn. `eventType` là khoá của `NOTIFICATION_TEMPLATE_REGISTRY` + `EVENT_TO_TAB` + `EVENT_REGISTRY`. */
  | { readonly status: "live"; readonly eventType: string }
  /** Đã có thông báo KHÁC phủ đúng tình huống — bắn thêm là hai tin cho một việc. */
  | { readonly status: "covered"; readonly coveredBy: string; readonly why: string }
  /** Chưa ai lo. Khai ra thì khoảng trống có ĐỊA CHỈ; im lặng thì không test nào canh được sự vắng mặt. */
  | { readonly status: "silent"; readonly owner: string; readonly why: string };

/**
 * 🔴 `satisfies Readonly<Record<GuidePushTrigger, …>>` là chốt chặn THẬT: thêm một trigger `push` thứ mười vào
 * `GUIDE_TRIGGER_CHANNEL` mà quên phân loại ở đây ⇒ **lỗi biên dịch**, mạnh hơn mọi test.
 */
export const GUIDE_NOTIFY_CHANNEL_SPEC = Object.freeze({
  /**
   * 🟢 SỐNG — **đợt thu đã thu đủ**. Hôm nay repo có 57 event outbox và **không cái nào** nói câu này: người ta biết ai vừa
   * đóng (`payment.confirmed`, tin riêng của NGƯỜI ĐÓNG) nhưng không ai biết **đợt đã xong**. Đó là lý do nó là tín hiệu đầu
   * tiên của kênh: nó KHÔNG chồng lên bất kỳ tin nào đang có, và nó là tin hiếm hoi mang tin TỐT.
   */
  MILESTONE: { status: "live", eventType: "ai.guide.milestone" },
  /**
   * 🔴 ĐÃ CÓ NGƯỜI LÀM — `reminder.job.ts` quét `Collection.dueDate` theo ngưỡng `reminder_days_before`
   * (fund > system, mặc định 3) và bắn `due.reminder` **riêng từng người**, idempotent một lần/due (partial-unique
   * `outbox_events_due_reminder_unique`). Thêm một vòng quét hạn ở đây = hai lời nhắc cho một khoản nợ.
   */
  DEADLINE_NEAR: {
    status: "covered",
    coveredBy: "due.reminder",
    why: "reminder.job quét dueDate theo ngưỡng reminder_days_before, nhắc riêng từng người, idempotent một lần/due",
  },
  /**
   * 🔴 ĐÃ CÓ NGƯỜI LÀM — nghĩa vụ mới sinh ra khi đợt thu mở, và `collection.created` fan-out tới đúng những người có due
   * trong đợt đó. `DUTY_CREATED` vẫn sống ở kênh CHAT (thẻ `DUTY-MY-UNPAID`/`DUTY-CONFIRM-PENDING`) — hai kênh, một sự việc.
   */
  DUTY_CREATED: {
    status: "covered",
    coveredBy: "collection.created",
    why: "đợt thu mở ra nghĩa vụ, và collection.created đã fan-out tới đúng người có due trong đợt đó",
  },
  /**
   * 🔴 ĐÃ CÓ NGƯỜI LÀM — khoản của tôi được đối chiếu ⇒ `payment.confirmed` (tin riêng của người đóng);
   * bị từ chối ⇒ `payment.declined`. Cả hai đã ở tab "Lời nhắc" và có đích `collectionDetail`.
   */
  MY_ITEM_RESOLVED: {
    status: "covered",
    coveredBy: "payment.confirmed",
    why: "khoản của tôi được đối chiếu/từ chối đã có payment.confirmed + payment.declined, tin riêng của người đóng",
  },
  /**
   * ⚪ CHƯA AI LO — và đây là ô mà chính bảng này suýt tự mắc lỗi nó sinh ra để chặn (review #856).
   *
   * Bản đầu khai `covered: collection.created`. Nó chỉ **GẦN ĐÚNG**: đúng cho quỹ **định kỳ**
   * (`recurring-collection.job` sinh đợt thu ⇒ đợt đó phát `collection.created`), nhưng repo còn những
   * "kỳ" **KHÔNG đẻ đợt thu** — `HuiRound` (kỳ hụi) · `SubscriptionPool` (kỳ gói mua chung) ·
   * `MembershipPeriod`. Với chúng, "kỳ mới bắt đầu" hôm nay **không ai báo**.
   *
   * 🔴 Vì sao sai đó ĐẮT: trong bảng này `covered` không phải một ghi chú — nó là **lệnh CẤM bắn thêm**.
   * Khai `covered` cho một vế mới phủ được một nửa là **khoá luôn nửa chưa ai phủ**, và khoá nó bằng
   * chính cái guard dựng ra để chống bỏ sót. Một ô "gần đúng" ở đây tệ hơn một ô để trống.
   *
   * ⇒ `silent`, và `why` nói rõ **phần nào đã phủ, phần nào chưa** — để người sau không phải đo lại.
   */
  /**
   * ⚪ **QĐ PO 24/09/2026: KHÔNG làm trong đợt này** — hụi · gói mua chung · kỳ thành viên. Đây là một quyết định, không
   * phải một việc còn treo; bản trước của dòng này chỉ nói "chưa ai báo" nên nó đọc như một lỗ hổng cần vá.
   * Phần ĐÃ phủ vẫn đúng như đo ở review #856 và vẫn quan trọng: đừng đọc QĐ này thành "kỳ mới không ai báo bao giờ".
   */
  PERIOD_START: {
    status: "silent",
    owner: "US-AIV2-0065",
    why: "QĐ PO 24/09 KHÔNG làm đợt này (hụi/gói mua chung/kỳ thành viên); quỹ ĐỊNH KỲ vẫn có collection.created phủ như cũ",
  },
  /**
   * ⚪ CHƯA AI LO **ở kênh PUSH** — nhưng kênh CHAT đã có người lo (`RESUME-UNFINISHED` — US-AIV2-tourguide-0007a: mời «Làm tiếp» phiếu dở ở lời chào kế, mọi tuổi).
   *
   * 🔴 Hai kênh, hai câu hỏi (luật đầu file). Phiếu bỏ dở là một việc người dùng tự bỏ giữa chừng; đẩy một **thông báo
   * hệ thống** về nó là app đi nhắc người ta về chính sự do dự của họ — khác hẳn nhắc một khoản phải đóng, nơi có người
   * khác đang chờ. Nên chat nói (người dùng chủ động mở ra), push im.
   */
  STALE: {
    status: "silent",
    owner: "US-AIV2-0065",
    why: "kênh CHAT đã lo (thẻ RESUME-UNFINISHED mời «Làm tiếp» ở lời chào kế — 0007a, QĐ PO Q5); push im có chủ — phiếu bỏ dở không có người thứ hai đang chờ",
  },
  /**
   * ⚪ CHƯA AI LO **ở kênh PUSH** — kênh CHAT đã có `ROLE-CHANGED-REVIEW` từ `0065`.
   *
   * Vì sao không nối luôn push: nó đòi một producer ở `RoleManagementService` (đường GHI, `@gate` RBAC) cộng một template
   * mới trong `NOTIFICATION_TEMPLATE_REGISTRY` theo locale NGƯỜI NHẬN. Kênh chat với tới được cùng sự việc bằng một phép
   * đếm ở lượt mở, 0 dòng mã ở đường ghi tiền. Nối push là một QĐ riêng, không phải phần còn lại của task này.
   */
  MY_ROLE_CHANGED: {
    status: "silent",
    owner: "US-AIV2-0065",
    why: "kênh CHAT đã lo (thẻ ROLE-CHANGED-REVIEW); push đòi producer ở đường GHI RBAC + template theo locale — một QĐ riêng",
  },
  /**
   * ⚪ CHƯA AI LO **ở kênh PUSH** — kênh CHAT đã có `CONFIG-CHANGED-REVIEW` từ `0065`, giới hạn trong **4 khoá PO duyệt**
   * (`GUIDE_CONFIG_WATCHED_ACTIONS`, allowlist ĐÓNG).
   *
   * Câu cũ ở đây — *"cần PO duyệt từng khoá"* — nay ĐÃ CÓ câu trả lời; giữ nguyên nó là để một việc đã xong trông như
   * còn treo. Nhưng vế push vẫn im: một thông báo hệ thống về cài đặt nhóm gửi cho cả 30 người là thứ dễ bị tắt nhất,
   * và tắt một lần thì tin THẬT về số tài khoản nhận tiền cũng tắt theo.
   */
  CONFIG_CHANGED: {
    status: "silent",
    owner: "US-AIV2-0065",
    why: "kênh CHAT đã lo (thẻ CONFIG-CHANGED-REVIEW, whitelist 4 khoá PO duyệt 24/09); push im có chủ — tin cài đặt gửi cả nhóm là tin dễ bị tắt nhất",
  },
  /** ⚪ CHƯA AI LO — rủi ro khôi phục tài khoản; chạm bảo mật nên cần một QĐ riêng, không tiện tay thêm vào đây. */
  ACCOUNT_RISK: {
    status: "silent",
    owner: "US-AIV2-0065",
    why: "chạm bảo mật tài khoản, cần một quyết định riêng chứ không phải một dòng thêm vào bảng này",
  },
} as const satisfies Readonly<Record<GuidePushTrigger, GuideNotifyState>>);

/** `eventType` của trigger đang SỐNG; `null` cho `covered`/`silent` — caller phải phân biệt được hai ca đó. */
export function guideNotifyEventType(trigger: GuidePushTrigger): string | null {
  const spec: GuideNotifyState = GUIDE_NOTIFY_CHANNEL_SPEC[trigger];
  return spec.status === "live" ? spec.eventType : null;
}

/** Suy TỪ bảng — một danh sách chép tay ở đây sẽ mục ngay lần trigger thứ hai lên `live`. */
export const GUIDE_NOTIFY_LIVE_EVENT_TYPES: readonly string[] = Object.freeze(
  GUIDE_PUSH_TRIGGERS.map((t) => guideNotifyEventType(t)).filter((e): e is string => e !== null),
);

/** Khoá `system_settings` bật kênh. 🔴 MẶC ĐỊNH TẮT ⇒ 0 hồi quy: hệ thống đang chạy không tự nhiên phun tin mới. */
export const GUIDE_NOTIFY_ENABLED_KEY = "ai_guide_notify_enabled";
export const GUIDE_NOTIFY_ENABLED_DEFAULT = false;

/**
 * 🔴🔴 RIÊNG TƯ BẰNG **HÌNH DẠNG**, KHÔNG BẰNG PHÉP QUÉT CHỮ (P5.3 · `CLAUDE.md` §7).
 *
 * Kênh NHẮC là riêng tư tuyệt đối: app **không bao giờ** đưa tên một người vào tin của người khác. Một phép quét chữ
 * ("payload có chứa tên nào không") là bất khả thi — tên tiếng Việt không cạo sạch được, và chính `CLAUDE.md` §4 đã ghi
 * nhận điều đó thành một QĐ PO. Nên luật ở đây đảo chiều: **payload chỉ được mang ID và tên trigger**, nên một cái tên
 * *không biểu diễn được*. Allowlist ĐÓNG, không blacklist.
 *
 * Hệ quả có chủ đích: câu chữ của tin **không** đi trong payload. Handler tra tên đợt thu từ DB ngay trước khi render
 * (đúng khuôn `dispatchCollectionCreated`), và câu chữ lấy từ `NOTIFICATION_TEMPLATE_REGISTRY` theo **locale người nhận**.
 *
 * `groupId` BẮT BUỘC vì `notification-target-payload.spec.ts` AC2 đòi mọi tin HIỆN ở hộp thư phải mang nó;
 * `collectionId` BẮT BUỘC vì đích của tin là `collectionDetail` (AC4 của cùng lưới).
 */
export const GUIDE_NOTIFY_PAYLOAD_KEYS = ["trigger", "groupId", "fundId", "collectionId"] as const;

export class GuideNotifyPayloadError extends Error {
  constructor(readonly reason: string) {
    super(`GUIDE_NOTIFY_PAYLOAD_NOT_PRIVATE: ${reason}`);
    this.name = "GuideNotifyPayloadError";
  }
}

/**
 * Hình của một id: uuid v1–v8 (`CLAUDE.md` §7 — nibble `[1-8]`, KHÔNG `[1-5]`; 8 bảng ghi-nhiều dùng uuid v7).
 * Cố ý KHÔNG dùng `isUuid()` của contracts: `packages/domain` không dep contracts, và một chuỗi rỗng/chữ tự do phải trượt.
 */
const GUIDE_NOTIFY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function assertGuideNotifyPayloadPrivate(payload: unknown): void {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) {
    throw new GuideNotifyPayloadError("payload phải là một object thuần");
  }
  const rec = payload as Record<string, unknown>;
  for (const key of Object.keys(rec)) {
    if (!(GUIDE_NOTIFY_PAYLOAD_KEYS as readonly string[]).includes(key)) {
      // Allowlist ĐÓNG: khoá lạ bị chặn dù tên nó vô hại. Đây là chỗ một trường chữ tự do sẽ cố lọt vào.
      throw new GuideNotifyPayloadError(`khoá ngoài allowlist: ${key}`);
    }
  }
  for (const key of GUIDE_NOTIFY_PAYLOAD_KEYS) {
    const value = rec[key];
    if (typeof value !== "string") throw new GuideNotifyPayloadError(`thiếu khoá bắt buộc: ${key}`);
    if (key === "trigger") {
      if (!isGuideTrigger(value)) throw new GuideNotifyPayloadError(`trigger không thuộc whitelist: ${value}`);
      continue;
    }
    if (!GUIDE_NOTIFY_ID_RE.test(value)) throw new GuideNotifyPayloadError(`${key} không phải uuid`);
  }
}
